import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import RemoveWordDialog from "../../pzl_common/verify_coord/RemoveWordDialog";
import Logger from "../../../services/Logger";
import AdminCrossService from "./AdminCrossService";

const DRAFT = "DRAFT";
const READY_TO_REVIEW = "READY_TO_REVIEW";
const REVIEWED = "REVIEWED";
const PUBLISHED = "PUBLISHED";

const STATE_ROUTE_MAP = {
    [DRAFT]: "/admin/puzzle/cross_board/draft",
    [READY_TO_REVIEW]: "/admin/puzzle/cross_board/ready-to-review",
    [REVIEWED]: "/admin/puzzle/cross_board/reviewed",
    [PUBLISHED]: "/admin/puzzle/cross_board/published"
};

export default function AdminCrossTplVerifyCoord() {
    const navigate = useNavigate();
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const fetchResults = useCallback(async () => {
        setLoading(true);
        try {
            const result = await AdminCrossService.verifyFilledCoord();
            const items = Array.isArray(result) ? result.filter((r) => r && r.tplId) : [];
            setResults(items);
        } catch (err) {
            Logger.error("Error verifying filled coord:", err);
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchResults();
    }, [fetchResults]);

    const handleRowClick = (row) => {
        setSelectedRow(row);
        setRemoveDialogOpen(true);
    };

    const handleOpenInNewWindow = (e, row) => {
        e.stopPropagation();
        const base = STATE_ROUTE_MAP[row.state] || STATE_ROUTE_MAP[DRAFT];
        const url = `${window.location.origin}${base}/${row.tplId}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <Button variant="outlined" onClick={() => navigate("/admin/puzzle/cross_tpl")} size="small">
                    ← Back
                </Button>
                <Typography variant="h6">Verify Coordinates — Puzzles with words to remove (no exclusive cells)</Typography>
                <Button variant="outlined" onClick={fetchResults} disabled={loading} size="small">
                    Refresh
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <CircularProgress />
                    <span>Verifying puzzles…</span>
                </Box>
            ) : results.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                    No puzzles found with words that must be removed.
                </Box>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>State</TableCell>
                            <TableCell>Language</TableCell>
                            <TableCell>Words to remove</TableCell>
                            <TableCell padding="none" width={48} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.map((row) => (
                            <TableRow
                                key={row.tplId}
                                hover
                                onClick={() => handleRowClick(row)}
                                sx={{ cursor: "pointer" }}
                            >
                                <TableCell>{row.title || "—"}</TableCell>
                                <TableCell>{row.state || "—"}</TableCell>
                                <TableCell>{row.language || "—"}</TableCell>
                                <TableCell>
                                    {Array.isArray(row.words) && row.words.length > 0
                                        ? row.words.join(", ")
                                        : "—"}
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                    <Tooltip title="Open in new tab">
                                        <IconButton size="small" onClick={(e) => handleOpenInNewWindow(e, row)}>
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <RemoveWordDialog
                open={removeDialogOpen}
                onClose={() => {
                    setRemoveDialogOpen(false);
                    setSelectedRow(null);
                }}
                tplId={selectedRow?.tplId}
                wordsToRemove={selectedRow?.words || []}
                onRefresh={fetchResults}
            />
        </Box>
    );
}
